import { Link, useLocation, useNavigate } from 'react-router-dom'
import { t } from '../i18n.ts'
import { useIncomingCount } from '../store/StoreProvider.tsx'
import { SignInAction } from './AuthButton.tsx'
import { ImportSchedule } from './ImportSchedule.tsx'
import './AppHeader.css'

/**
 * The persistent top bar.
 *
 * In standalone display mode there is no browser chrome, so without an in-app back
 * control a user who opens a concert has no way out. Rendered on every route: the mark
 * always, the wordmark beside it on the day view, and a back control on the sub-routes.
 */
export function AppHeader() {
  const location = useLocation()
  const navigate = useNavigate()

  // One definition of "incoming", owned by the store and re-rendered by its version
  // counter — see `useIncomingCount`.
  const incoming = useIncomingCount()

  const isSubPage =
    location.pathname.startsWith('/konsert/') ||
    location.pathname.startsWith('/venner') ||
    location.pathname.startsWith('/kart')

  function goBack() {
    // A deep link opened from an email or a notification has no in-app history to
    // pop, so fall back to the programme rather than leaving the user stranded.
    if (window.history.length > 1) navigate(-1)
    else navigate('/')
  }

  return (
    <header className="app-header">
      <div className="app-header__inner">
        <div className="app-header__lead">
          {/*
            The mark, on every route, always pointing at today.

            It used to appear only on the day view, so the one thing on screen that
            never moves — the corner you look at to remember which app you are in —
            went missing on three routes out of four. `/` is the target rather than a
            day: it redirects to whichever day the festival is on right now, so this
            says «today» permanently instead of freezing whatever today meant when the
            page was rendered.

            Beside the back control rather than instead of it. They answer different
            questions — «where was I» and «take me to the programme» — and a concert
            reached from a friend's share is exactly the case where collapsing the two
            would strand you: back goes to the friends page, the mark does not.
          */}
          <Link
            to="/"
            className="app-header__brand"
            // On the day view the wordmark beside it is the name; here there is
            // nothing else in the link to read, and the mark itself is decorative.
            aria-label={isSubPage ? t.nav.today : undefined}
          >
            <span className="app-header__mark" aria-hidden="true">
              Ø
            </span>
            {!isSubPage && <span className="app-header__title">{t.appName}</span>}
          </Link>

          {isSubPage && (
            <button
              type="button"
              className="app-header__back"
              // The name lives on the button, not on the label, because the label is
              // dropped on a narrow bar (AppHeader.css) and the arrow left standing
              // alone still has to say what it does.
              aria-label={t.nav.back}
              onClick={goBack}
            >
              <BackArrow />
              <span className="app-header__back-label">{t.nav.back}</span>
            </button>
          )}
        </div>

        <div className="app-header__actions">
          {/* Import first, then the map, then friends: the setup step done once before
              the festival, the ground you will be standing on, and last the people —
              which reads left to right as "bring a plan in, find your way around, see
              what friends sent". */}
          <ImportSchedule />

          <Link
            to="/kart"
            className="app-header__action"
            aria-label={t.nav.map}
            title={t.nav.map}
          >
            <MapIcon />
          </Link>

          <Link
            to="/venner"
            className="app-header__action"
            aria-label={incoming > 0 ? t.nav.friendsWithRequests(incoming) : t.nav.friends}
            title={t.nav.friends}
          >
            <FriendsIcon />
            {incoming > 0 && (
              // Hidden from the accessible tree on purpose: the number is already in
              // the link's own name, and announcing it twice reads as two counts.
              <span className="app-header__badge" aria-hidden="true">
                {incoming}
              </span>
            )}
          </Link>

          {/* Last, and the only filled thing in the bar, because for a signed-out
              reader it is the only control here that leads anywhere new. It never
              competes with the request badge for that attention: a badge counts
              friend requests, and there are none to count until somebody has an
              account. Signed in, this renders nothing and the bar is as it was. */}
          <SignInAction />
        </div>
      </div>
    </header>
  )
}

function BackArrow() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M15 5l-7 7 7 7"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** A folded paper map — the site plan, not a pin dropped on a street map. */
function MapIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 4.5L3.5 6.6v12.9L9 17.4l6 2.1 5.5-2.1V4.5L15 6.6 9 4.5zM9 4.5v12.9M15 6.6v12.9"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function FriendsIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM2.5 19.5c0-3 2.9-4.8 6.5-4.8s6.5 1.8 6.5 4.8M16.5 4.6a3.5 3.5 0 010 6.8M18 14.9c2.1.5 3.5 1.8 3.5 3.6"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
